import React from 'react';
import { useFont } from '../../Design/FontContext';

/**
 * 🎨 HighlightText Component - Wiederverwendbares Text-Highlight-System
 * 
 * Ermöglicht es, bestimmte Wörter in einem Text mit einer anderen Schriftart zu highlighten.
 * ✅ UNTERSTÜTZT: Reinen Text UND HTML-Content aus Rich Text Editoren
 * 
 * @param {string} text - Der Haupttext (kann HTML enthalten)
 * @param {string} highlightWords - Komma-getrennte Liste von Wörtern zum Highlighten
 * @param {object} baseStyle - Basis-Styling für den Text
 * @param {string} highlightFont - Font für highlighted Wörter (optional)
 * @param {string} highlightColor - Farbe für highlighted Wörter (optional)
 * @param {string} highlightWeight - Font-Weight für highlighted Wörter (optional)
 * @param {string} highlightSize - Font-Size für highlighted Wörter (optional)
 * @param {string} highlightSizeMobile - Mobile Font-Size für highlighted Wörter (optional)
 * @param {string} highlightSizeBig - Big Screen Font-Size für highlighted Wörter (optional)
 * @param {string} highlightEffect - Text-Effect für highlighted Wörter (optional)
 * @param {number} highlightMarginLeft - Margin Links für highlighted Wörter (optional)
 * @param {number} highlightMarginRight - Margin Rechts für highlighted Wörter (optional)
 * @param {boolean} caseSensitive - Groß-/Kleinschreibung beachten (default: false)
 * @param {boolean} isHtmlContent - Ob der Text HTML-Content enthält (default: false)
 */
function HighlightText({ 
    text = '', 
    highlightWords = '', 
    baseStyle = {}, 
    highlightFont = '',
    highlightColor = '',
    highlightWeight = '',
    highlightSize = '',
    highlightSizeMobile = '',
    highlightSizeBig = '',
    highlightEffect = '',
    highlightMarginLeft = 0,
    highlightMarginRight = 0,
    caseSensitive = false,
    getTextEffectStyles = null,
    isHtmlContent = false // ✅ NEUE PROP für HTML-Content Support
}) {
    const fonts = useFont();

    // Wenn keine Highlight-Wörter definiert sind, gib normalen Text zurück
    if (!highlightWords || !text) {
        if (isHtmlContent) {
            return <span style={baseStyle} dangerouslySetInnerHTML={{ __html: text }} />;
        }
        return <span style={baseStyle}>{text}</span>;
    }

    // Parse Highlight-Wörter (komma-getrennt, trimme Leerzeichen)
    const wordsToHighlight = highlightWords
        .split(',')
        .map(word => word.trim())
        .filter(word => {
            // ✅ BEREINIGUNG: Filtere leere, nur-Bindestrich oder problematische Wörter
            if (word.length === 0) return false;
            if (word === '-' || word === '--') return false; // Nur Bindestriche
            if (word.match(/^[^\w\s-]+$/)) return false; // Nur Sonderzeichen
            return true;
        });

    if (wordsToHighlight.length === 0) {
        if (isHtmlContent) {
            return <span style={baseStyle} dangerouslySetInnerHTML={{ __html: text }} />;
        }
        return <span style={baseStyle}>{text}</span>;
    }

    // Erstelle Regex-Pattern für alle Highlight-Wörter
    const escapeRegExp = (string) => {
        // ✅ DASH-BUG FIX: Bindestrich (-) muss speziell escaped werden
        return string.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
    };

    const pattern = wordsToHighlight
        .map(word => escapeRegExp(word))
        .join('|');
    
    const regex = new RegExp(`(${pattern})`, caseSensitive ? 'g' : 'gi');

    // ✅ HTML-CONTENT VERARBEITUNG
    if (isHtmlContent) {
        // Für HTML-Content: Highlights direkt im HTML anwenden
        let processedHtml = text;
        
        wordsToHighlight.forEach(word => {
            const wordRegex = new RegExp(`\\b(${escapeRegExp(word)})\\b`, caseSensitive ? 'g' : 'gi');
            
            // ✅ TEXT EFFECTS für HTML-Content: Generiere CSS-Styles aus getTextEffectStyles
            let textEffectStyles = '';
            if (highlightEffect && getTextEffectStyles) {
                const effectStyles = getTextEffectStyles(highlightEffect);
                if (effectStyles) {
                    // Konvertiere React-Style-Objekt zu CSS-String
                    textEffectStyles = Object.entries(effectStyles)
                        .map(([key, value]) => {
                            // Konvertiere camelCase zu kebab-case für CSS
                            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                            return `${cssKey}: ${value};`;
                        })
                        .join(' ');
                }
            }
            
            // Erstelle Highlight-Span mit allen Styles inkl. Text Effects
            const highlightSpan = `<span style="
                ${highlightFont ? `font-family: ${highlightFont};` : ''}
                ${highlightColor ? `color: ${highlightColor};` : ''}
                ${highlightWeight ? `font-weight: ${highlightWeight};` : ''}
                ${highlightSize ? `font-size: ${highlightSize}px;` : ''}
                ${highlightMarginLeft ? `margin-left: ${highlightMarginLeft}px;` : ''}
                ${highlightMarginRight ? `margin-right: ${highlightMarginRight}px;` : ''}
                ${textEffectStyles}
            ">$1</span>`;
            
            processedHtml = processedHtml.replace(wordRegex, highlightSpan);
        });
        
        return <span style={baseStyle} dangerouslySetInnerHTML={{ __html: processedHtml }} />;
    }

    // ✅ NORMALER TEXT-VERARBEITUNG (bestehende Logik)
    const parts = text.split(regex);

    // Highlight-Styling mit Responsive Font-Größen und Margins
    const highlightStyle = {
        ...baseStyle,
        ...(highlightFont && { fontFamily: highlightFont || fonts.globalBigFont }),
        ...(highlightColor && { color: highlightColor }),
        ...(highlightWeight && { fontWeight: highlightWeight }),
        ...(highlightSize && { 
            fontSize: `${highlightSize}px`,
            // ✅ RESPONSIVE FONT-SIZES für Highlights
            '--mobile-size': `${highlightSizeMobile || highlightSize}px`,
            '--normal-size': `${highlightSize}px`,
            '--big-size': `${highlightSizeBig || highlightSize}px`
        }),
        // ✅ MARGIN CONTROLS für Highlight Words
        marginLeft: `${highlightMarginLeft}px`,
        marginRight: `${highlightMarginRight}px`,
        ...(highlightEffect && getTextEffectStyles && getTextEffectStyles(highlightEffect))
    };

    return (
        <span style={baseStyle}>
            {parts.map((part, index) => {
                // Prüfe ob dieser Teil ein Highlight-Wort ist
                const isHighlight = wordsToHighlight.some(word => 
                    caseSensitive ? part === word : part.toLowerCase() === word.toLowerCase()
                );

                if (isHighlight) {
                    return (
                        <span key={index} style={highlightStyle}>
                            {part}
                        </span>
                    );
                } else {
                    return <span key={index}>{part}</span>;
                }
            })}
        </span>
    );
}

export default HighlightText;
